import React, { createContext, useContext } from 'react';
import { useBrandProductsEnhanced, type BrandProductHookEnhanced, type BrandProductCategory, type BrandProductSubcategory } from '@/hooks/useBrandProductsEnhanced';
import { useBrandManager } from '@/hooks/useBrandManager';

interface BrandProductsDataContextType {
  products: BrandProductHookEnhanced[] | undefined;
  categories: BrandProductCategory[] | undefined;
  subcategories: BrandProductSubcategory[] | undefined;
  isLoading: boolean;
  error: Error | null;
  createCategory: ReturnType<typeof useBrandProductsEnhanced>['createCategory'];
  createSubcategory: ReturnType<typeof useBrandProductsEnhanced>['createSubcategory'];
  updateCategory: ReturnType<typeof useBrandProductsEnhanced>['updateCategory'];
  updateSubcategory: ReturnType<typeof useBrandProductsEnhanced>['updateSubcategory'];
  deleteCategory: ReturnType<typeof useBrandProductsEnhanced>['deleteCategory'];
  deleteSubcategory: ReturnType<typeof useBrandProductsEnhanced>['deleteSubcategory'];
  bulkImport: ReturnType<typeof useBrandProductsEnhanced>['bulkImport'];
  isCreatingCategory: boolean;
  isCreatingSubcategory: boolean;
  isUpdatingCategory: boolean;
  isUpdatingSubcategory: boolean;
  isDeletingCategory: boolean;
  isDeletingSubcategory: boolean;
  isImporting: boolean;
  importResult: any;
  canDeleteCategory: (categoryId: string) => boolean;
  canDeleteSubcategory: (subcategoryId: string) => boolean;
}

const BrandProductsDataContext = createContext<BrandProductsDataContextType | undefined>(undefined);

export function BrandProductsDataProvider({ children }: { children: React.ReactNode }) {
  const { currentBrandId } = useBrandManager();
  const data = useBrandProductsEnhanced(currentBrandId || '');

  return (
    <BrandProductsDataContext.Provider value={data}>
      {children}
    </BrandProductsDataContext.Provider>
  );
}

export function useBrandProductsData() {
  const context = useContext(BrandProductsDataContext);
  if (!context) {
    throw new Error('useBrandProductsData must be used within BrandProductsDataProvider');
  }
  return context;
}
